/**
 * WHAT EACH VERTICAL IS (ARQUITECTURA §1). Every question the product asks of
 * a business type — its modules, faces, words, price list, categories — is
 * answered here and nowhere else: the functions in business-type.ts, faces.ts,
 * type-words.ts and catalog-shape.ts are views of it.
 *
 * A type is a way of working (`WAYS`: how it behaves) and its own data
 * (`VERTICAL_DATA`: how it speaks); `VERTICALS` joins the two once, and is
 * what every reader reads. Adding a vertical on an existing way is one entry
 * in `VERTICAL_DATA` and its sample in demo/scenarios.ts.
 */
import { ASSET_CATEGORIES, assetSentences } from "./asset.js";
import { BANK_FEE_CATEGORY } from "./bank-fee.js";
// THE ORDER IS THE WIZARD'S LIST: the campaign's trades
// first -- the workshop and the distributor -- then every type born collecting
// cuotas, then the rest; «Otro» last, the fallback rather than an option among
// equals. A new type goes where it should be listed.
export const BUSINESS_TYPES = [
    "Taller o servicio técnico",
    "Distribuidora",
    "Guardería",
    "Academia deportiva",
    "Academia de arte o danza",
    "Centro de tutorías",
    "Transporte escolar",
    "Gimnasio",
    "Barbería o salón de belleza",
    "Panadería o repostería",
    "Restaurante o fonda",
    "Imprenta",
    "Clínica o consultorio",
    // The professions, under «Negocio» like any trade; «Otro profesional» last of them.
    "Contabilidad",
    "Abogado",
    "Arquitectura o ingeniería",
    "Diseño o publicidad",
    "Consultoría",
    "Otro profesional",
    // Not one kind of shop among the others: the church and personal books are
    // families of their own, and the car wash is the one trade with a module
    // nobody else may switch on.
    "Iglesia",
    "Finanzas personales",
    "Lavado de autos",
    "Otro",
];
export function isBusinessType(value) {
    return BUSINESS_TYPES.includes(value);
}
/** What a type the list does not know reads as — and the type a business with none is saved under. */
export const FALLBACK_TYPE = "Otro";
/** Where an employee's pay lands when the work does not assign it. It names a row that gets written. */
export const SALARY_CATEGORY = "Sueldos";
/**
 * Where the interest a bank pays into an account lands (spec 2026-10-17 §4.1
 * «Ajustes del banco»): every seed's IN side has it,
 * just before its last «Otros», and 0121 backfills it. It names a row that gets written.
 */
export const INTEREST_CATEGORY = "Intereses";
// The workshop's job is a sale, not a pedido or a «ticket»: «Nueva venta»
// writes it, and the word comes from the dictionary. It is feminine, so it is closed
// («Cerrada»), not delivered. The print shop and «Otro» quote and sell like
// the workshop, so they say the same words.
const SALE_WORDS = {
    orders: "Ventas",
    order: "venta",
    orderFeminine: true,
    delivered: "Cerrada",
    deliverAction: "Cerrar venta",
    deliverActionShort: "Cerrar",
    undeliverAction: "Deshacer cierre",
};
/**
 * What a profession says: its work is finished, not delivered, and is called
 * by its trade's word; its price list is of services.
 */
function professionWords(orders, order) {
    return {
        catalog: "Servicios y precios",
        catalogItem: "servicio",
        delivered: "Terminado",
        deliverActionShort: "Terminar",
        undeliverAction: "Deshacer",
        orderFeminine: false,
        orders,
        order,
        deliverAction: `Terminar ${order}`,
    };
}
const PROFESSIONAL_CATEGORIES = {
    IN: ["Honorarios", INTEREST_CATEGORY, "Otros"],
    OUT: ["Servicios", "Alquiler", "Transporte", "Suscripciones", SALARY_CATEGORY, BANK_FEE_CATEGORY, "Otros"],
};
/**
 * WHAT EVERY PROFESSION BRINGS OVER ITS WAY (spec 2026-10-23 §3.6): fees in and
 * an office's costs out, its own name rather than a shop's, and no sample --
 * «Comenzar» on the last question creates the practice. Spread into each
 * profession's row, so a new one cannot forget any of the four.
 */
const PROFESSION_DATA = {
    categories: PROFESSIONAL_CATEGORIES,
    nameQuestion: "¿Con qué nombre trabajas?",
    namePrefill: "account",
    offersSample: false,
};
// What most ways are: a business with the full price list, nobody
// assigned and nothing of the customer's in the shop. Each way says how it
// differs.
const BASE_WAY = {
    family: "business",
    catalog: "full",
    words: {},
    assignsWork: false,
    servicesAssets: false,
    logbook: false,
    profile: { jobs: "none", reviews: "none" },
};
/** THE WAYS OF WORKING, one per distinct behaviour: every business type works as exactly one (`VERTICAL_DATA`). */
export const WAYS = {
    workshop: {
        ...BASE_WAY,
        // A workshop sells cases: a quote, a sale, a job and a close.
        modules: ["pedidos"],
        faces: ["SERVICIO"],
        words: SALE_WORDS,
        assignsWork: true,
        servicesAssets: true,
        logbook: true,
        startsAt: "later",
        profile: { jobs: "work", reviews: "work" },
    },
    // IT QUOTES AND SELLS (spec 2026-10-23 §3): the distributor, the print shop,
    // the professions and «Otro». The sale is born done, nobody is assigned and nothing
    // of the client's is in the shop, so a closed sale is followed by another,
    // never by a visit; its customers, once let in, see their quotes and sales.
    // Every delivered sale is a job, since none waits to be done. Inventario is
    // an add-on it is not born with. The default words are a distributor's:
    // «Pedidos», «Entregar», «Entregado»; each rubro brings its own.
    sells: {
        ...BASE_WAY,
        modules: ["pedidos"],
        faces: ["SERVICIO"],
        anotherVisit: false,
        clientSeesDocuments: true,
        profile: { jobs: "all", reviews: "all" },
    },
    // A place built to charge monthly dues always charges them, so asking was
    // noise, not a choice.
    dues: { ...BASE_WAY, modules: ["cuotas"], faces: ["ENCARGO"] },
    agenda: {
        ...BASE_WAY,
        // It sells the hour, and the hour is booked: it opens the agenda and nothing else.
        modules: ["citas"],
        // The hour is booked, never sold across a counter.
        faces: ["CITA"],
        // The order is «la cita», attended rather than delivered, and the list says what it holds.
        words: {
            orders: "Citas",
            order: "cita",
            orderFeminine: true,
            delivered: "Atendida",
            deliverAction: "Marcar atendida",
            deliverActionShort: "Atender",
            undeliverAction: "Deshacer",
            catalog: "Servicios y precios",
            catalogItem: "servicio",
            agenda: "Agenda",
        },
    },
    bakery: { ...BASE_WAY, modules: ["pedidos"], faces: ["ENCARGO"], catalog: "bakery" },
    restaurant: {
        ...BASE_WAY,
        modules: ["pedidos"],
        // Only payroll: a menu is not an inventory.
        addOns: ["planilla", "bancos"],
        // The table, the takeaway and the delivery; the custom order left the restaurant
        // (old ones still read by their own frozen face).
        faces: ["MESA", "LLEVAR", "DELIVERY"],
        catalog: "menu",
    },
    church: {
        ...BASE_WAY,
        family: "church",
        // The kernel as it is, and «Bancos y caja»: the only type born with it
        // (spec §4.0) -- a treasurer answers for each account.
        modules: ["bancos"],
        faces: ["ENCARGO"],
    },
    // Default words on purpose: every screen that would read them is closed in this family.
    personal: { ...BASE_WAY, family: "personal", modules: [], faces: ["ENCARGO"] },
    carWash: {
        ...BASE_WAY,
        // A wash is a sale of the common document (spec 2026-10-03 «El lavado se
        // reescribe»): no module of its own, only its face and its entry form.
        modules: ["pedidos"],
        // Payroll only: its products are free text, never counted.
        addOns: ["planilla", "bancos"],
        faces: ["LAVADO"],
        catalog: "wash",
        assignsWork: true,
        servicesAssets: true,
        assetCategories: ["VEHICLE"],
        plateSale: {
            worker: { label: "Lavador", missing: "Elige quién lo lavó." },
        },
        profile: { jobs: "all", reviews: "all" },
    },
};
/** Every way, in the order `WAYS` declares them. */
export const WAY_IDS = Object.keys(WAYS);
const VERTICAL_DATA = {
    "Taller o servicio técnico": {
        way: "workshop",
        assistantAsks: ["Agrega cambio de aceite a 35", "Pedro me quedó debiendo 60 de la reparación", "¿Cómo paso una cotización a venta?"],
        listIcon: "wrench",
        listExamples: "aires, refrigeración, electrónica, celulares, mecánica",
        catalogGuide: { name: "Cambio de aceite", why: "¿Cambia el precio según el material?", variants: ["Mineral", "Sintético"] },
        // The technicians.
        workerWords: { one: "técnico", many: "técnicos" },
        profile: { schemaType: "HomeAndConstructionBusiness" },
        assetWords: { one: "equipo", many: "equipos" },
        categories: {
            // «Repuestos» on both sides: the part is bought for the job and billed back with it.
            IN: ["Reparaciones", "Repuestos", INTEREST_CATEGORY, "Otros"],
            OUT: ["Repuestos", "Alquiler", "Servicios", "Sueldos", BANK_FEE_CATEGORY, "Otros"],
        },
    },
    "Distribuidora": {
        way: "sells",
        assistantAsks: ["Agrega arroz 5 lb a 4.50", "La tienda La Esquina me debe 120", "¿Cómo veo quién me debe más?"],
        listIcon: "box",
        listExamples: "mayorista, importadora, venta por catálogo",
        catalogGuide: { name: "Arroz 5 lb", why: "¿Lo vendes suelto y por caja?", variants: ["Unidad", "Caja de 12"] },
        profile: { schemaType: "WholesaleStore" },
        categories: {
            // What it buys is merchandise and what moves it is a truck.
            IN: ["Ventas", INTEREST_CATEGORY, "Otros"],
            OUT: ["Mercancía", "Combustible", "Mantenimiento", SALARY_CATEGORY, "Alquiler", "Servicios", BANK_FEE_CATEGORY, "Otros"],
        },
    },
    "Guardería": {
        way: "dues",
        assistantAsks: ["Anota un gasto de 35 en pañales", "¿Quién no ha pagado la mensualidad?", "¿Cómo inscribo a un niño en un plan?"],
        listIcon: "balloon",
        recipient: { singular: "niño o niña", plural: "Niños" },
        inventoryPitch: "Pañales, uniformes y lo que vendes aparte",
        catalogGuide: { name: "Mensualidad", why: "¿Cobras distinto según el horario?", variants: ["Medio día", "Día completo"] },
        profile: { schemaType: "ChildCare" },
    },
    "Academia deportiva": {
        way: "dues",
        assistantAsks: ["Anota un gasto de 60 en balones", "¿Quién no ha pagado la mensualidad?", "¿Cómo cobro la mensualidad?"],
        listIcon: "ball",
        listExamples: "fútbol, natación, artes marciales",
        recipient: { singular: "alumno", plural: "Alumnos" },
        inventoryPitch: "Uniformes, balones y lo que vendes aparte",
        catalogGuide: { name: "Mensualidad", why: "¿Cobras distinto según los días?", variants: ["2 días por semana", "3 días por semana"] },
        profile: { schemaType: "SportsActivityLocation" },
    },
    "Academia de arte o danza": {
        way: "dues",
        assistantAsks: ["Anota un gasto de 25 en vestuario", "¿Quién no ha pagado la mensualidad?", "¿Cómo cobro la mensualidad?"],
        listIcon: "palette",
        listExamples: "música, baile, pintura",
        recipient: { singular: "alumno", plural: "Alumnos" },
        inventoryPitch: "Uniformes, zapatillas y lo que vendes aparte",
        catalogGuide: { name: "Clase de ballet", why: "¿Cobras por clase o por mes?", variants: ["Por clase", "Mensual"] },
        profile: { schemaType: "LocalBusiness" },
    },
    "Centro de tutorías": {
        way: "dues",
        assistantAsks: ["Anota un gasto de 15 en copias", "¿Quién no ha pagado la mensualidad?", "¿Cómo cobro la mensualidad?"],
        listIcon: "book",
        listExamples: "refuerzo escolar, idiomas",
        recipient: { singular: "alumno", plural: "Alumnos" },
        inventoryPitch: "Libros, material y lo que vendes aparte",
        catalogGuide: { name: "Tutoría de matemáticas", why: "¿Cambia el precio según la duración?", variants: ["1 hora", "2 horas"] },
        profile: { schemaType: "EducationalOrganization" },
    },
    "Transporte escolar": {
        way: "dues",
        assistantAsks: ["Anota 40 de gasolina", "¿Quién no ha pagado la mensualidad?", "¿Cómo cobro el mes?"],
        listIcon: "bus",
        recipient: { singular: "estudiante", plural: "Estudiantes" },
        catalogGuide: { name: "Ruta escolar", why: "¿Cobras distinto por trayecto?", variants: ["Ida", "Ida y vuelta"] },
        profile: { schemaType: "LocalBusiness" },
    },
    "Gimnasio": {
        way: "dues",
        assistantAsks: ["Anota un gasto de 50 en mantenimiento", "¿Quién no ha pagado la mensualidad?", "¿Cómo cobro la mensualidad?"],
        listIcon: "dumbbell",
        inventoryPitch: "Suplementos, bebidas y lo que vendes aparte",
        catalogGuide: { name: "Membresía", why: "¿Tienes varios planes?", variants: ["Mensual", "Anual"] },
        profile: { schemaType: "ExerciseGym" },
    },
    "Barbería o salón de belleza": {
        way: "agenda",
        assistantAsks: ["Agrega corte de barba a 8", "Anota 20 en navajas", "¿Cómo agendo una cita?"],
        listIcon: "scissors",
        listExamples: "uñas, spa, maquillaje",
        catalogGuide: { name: "Corte de cabello", why: "¿Cambia el precio según el largo?", variants: ["Corto", "Largo"] },
        profile: { schemaType: "BeautySalon" },
        categories: {
            // «Servicios» is the chair and «Productos» the shelf: the words the trade uses.
            IN: ["Servicios", "Productos", INTEREST_CATEGORY, "Otros"],
            OUT: ["Insumos", "Alquiler", "Servicios", "Sueldos", BANK_FEE_CATEGORY, "Otros"],
        },
    },
    "Panadería o repostería": {
        way: "bakery",
        assistantAsks: ["Agrega pan de molde a 2.50", "Doña Carmen me quedó debiendo 15", "¿Cómo anoto un encargo para el sábado?"],
        listIcon: "ice-cream",
        listExamples: "pasteles, dulces por encargo",
        words: { prepIcon: "bakery" },
        catalogGuide: { name: "Pastel de chocolate", why: "¿Lo vendes en varios tamaños?", variants: ["Pequeño", "Grande"] },
        profile: { schemaType: "Bakery" },
    },
    "Restaurante o fonda": {
        way: "restaurant",
        assistantAsks: ["Agrega sopa de pollo a 5", "Anota 45 de compras en el mercado", "¿Cómo abro una mesa?"],
        listIcon: "utensils",
        listExamples: "cafetería, comida rápida, delivery",
        // A restaurant keeps accounts: «la cuenta», «abierta».
        words: {
            catalog: "Menú",
            catalogItem: "plato",
            orders: "Cuentas",
            order: "cuenta",
            orderFeminine: true,
            prep: "Cocina",
            prepExit: "Salir de la cocina",
            prepIcon: "kitchen",
        },
        catalogGuide: { name: "Arroz con pollo", why: "¿Lo sirves en varias porciones?", variants: ["Media", "Entera"] },
        profile: { schemaType: "Restaurant" },
        categories: {
            IN: ["Ventas", "Delivery", INTEREST_CATEGORY, "Otros"],
            OUT: ["Insumos", "Alquiler", "Servicios", "Sueldos", "Transporte", BANK_FEE_CATEGORY, "Otros"],
        },
    },
    "Imprenta": {
        way: "sells",
        assistantAsks: ["Agrega volantes a color a 25 el ciento", "Anota 80 de papel y tinta", "¿Cómo hago una cotización?"],
        listIcon: "printer",
        listExamples: "rotulación, sublimación, serigrafía",
        words: SALE_WORDS,
        catalogGuide: { name: "Tarjetas de presentación", why: "¿Cambia el precio según la cantidad?", variants: ["100 unidades", "500 unidades"] },
        profile: { schemaType: "LocalBusiness" },
    },
    "Clínica o consultorio": {
        way: "agenda",
        assistantAsks: ["Agrega consulta general a 30", "Anota 25 de insumos", "¿Cómo agendo una cita?"],
        listIcon: "medkit",
        listExamples: "médico, dentista, fisioterapia, psicología, nutrición",
        words: { parties: "Pacientes", party: "paciente", person: "profesional" },
        catalogGuide: { name: "Consulta general", why: "¿Cobras distinto según la consulta?", variants: ["Primera vez", "Control"] },
        profile: { schemaType: "MedicalClinic" },
        categories: {
            IN: ["Consultas", "Procedimientos", INTEREST_CATEGORY, "Otros"],
            OUT: ["Insumos", "Alquiler", "Servicios", SALARY_CATEGORY, BANK_FEE_CATEGORY, "Otros"],
        },
    },
    "Contabilidad": {
        ...PROFESSION_DATA,
        way: "sells",
        assistantAsks: ["Agrega declaración de renta a 150", "La empresa Ríos me debe 200 de octubre", "¿Cómo hago una cotización?"],
        listIcon: "calculator",
        words: professionWords("Trabajos", "trabajo"),
        catalogGuide: { name: "Contabilidad mensual", why: "¿Cobras distinto según el tamaño del negocio?", variants: ["Hasta 50 movimientos", "Más de 50 movimientos"] },
        profile: { schemaType: "AccountingService" },
        accountancy: true,
    },
    "Abogado": {
        ...PROFESSION_DATA,
        way: "sells",
        assistantAsks: ["Agrega redacción de contrato a 250", "El señor Gómez me debe 300 de honorarios", "¿Cómo hago una cotización?"],
        listIcon: "document",
        words: professionWords("Casos", "caso"),
        catalogGuide: { name: "Consulta legal", why: "¿Cobras distinto según la duración?", variants: ["30 minutos", "1 hora"] },
        profile: { schemaType: "Attorney" },
    },
    "Arquitectura o ingeniería": {
        ...PROFESSION_DATA,
        way: "sells",
        assistantAsks: ["Agrega levantamiento de planos a 500", "Anota 40 de impresión de planos", "¿Cómo hago una cotización?"],
        listIcon: "building",
        words: professionWords("Proyectos", "proyecto"),
        catalogGuide: { name: "Planos de vivienda", why: "¿Cambia el precio según el tamaño?", variants: ["Hasta 100 m²", "Más de 100 m²"] },
        profile: { schemaType: "ProfessionalService" },
    },
    "Diseño o publicidad": {
        ...PROFESSION_DATA,
        way: "sells",
        assistantAsks: ["Agrega diseño de logo a 150", "La tienda Bella me debe 150", "¿Cómo hago una cotización?"],
        listIcon: "brush",
        listExamples: "redes sociales, fotografía, video",
        words: professionWords("Proyectos", "proyecto"),
        catalogGuide: { name: "Diseño de logo", why: "¿Ofreces varios paquetes?", variants: ["Básico", "Completo"] },
        profile: { schemaType: "ProfessionalService" },
    },
    "Consultoría": {
        ...PROFESSION_DATA,
        way: "sells",
        assistantAsks: ["Agrega hora de consultoría a 60", "Anota 30 de transporte a una reunión", "¿Cómo hago una cotización?"],
        listIcon: "briefcase",
        listExamples: "marketing, recursos humanos, sistemas",
        words: professionWords("Proyectos", "proyecto"),
        catalogGuide: { name: "Hora de consultoría", why: "¿Cobras por hora o por proyecto?", variants: ["Por hora", "Por proyecto"] },
        profile: { schemaType: "ProfessionalService" },
    },
    // The profile names the family, never «Otro profesional».
    "Otro profesional": {
        ...PROFESSION_DATA,
        way: "sells",
        assistantAsks: ["Agrega un servicio de 50", "Anota un gasto de 20 en transporte", "¿Cómo hago una cotización?"],
        listIcon: "person",
        words: professionWords("Trabajos", "trabajo"),
        profile: { schemaType: "ProfessionalService", tradeName: "Profesional independiente" },
    },
    "Iglesia": {
        way: "church",
        assistantAsks: ["Anota una ofrenda de 150 del domingo", "Anota 60 de luz del templo", "¿Cómo hago la constancia de donaciones?"],
        listIcon: "church",
        words: {
            team: "Colaboradores",
            person: "colaborador",
            parties: "Miembros",
            party: "miembro",
            obligations: "Aportaciones",
            obligation: "aportación",
            obligationFeminine: true,
        },
        profile: { schemaType: "Church" },
        categories: {
            IN: ["Diezmos", "Ofrendas", "Misiones", "Construcción", INTEREST_CATEGORY, "Otros"],
            OUT: ["Servicios", "Alquiler", "Mantenimiento", "Actividades", "Ayudas", "Sueldos", BANK_FEE_CATEGORY, "Otros"],
        },
    },
    "Finanzas personales": {
        way: "personal",
        assistantAsks: ["Anota 45 del supermercado", "Le presté 50 a Carlos", "¿En qué gasto más?"],
        listIcon: "wallet",
        profile: { schemaType: "LocalBusiness" },
        // No SALARY_CATEGORY: this family has no employees.
        categories: {
            IN: ["Sueldo", "Extras", INTEREST_CATEGORY, "Otros"],
            OUT: ["Comida", "Casa", "Transporte", "Servicios", "Salud", "Educación", "Diversión", BANK_FEE_CATEGORY, "Otros"],
        },
    },
    "Lavado de autos": {
        way: "carWash",
        assistantAsks: ["Agrega lavado de motor a 15", "Anota 30 de jabón y cera", "¿Cómo anoto un lavado?"],
        listIcon: "car",
        words: { orders: "Lavados", order: "lavado", open: "En pista" },
        catalogGuide: { name: "Lavado completo", why: "¿Cobras distinto según el carro?", variants: ["Sedán", "SUV", "Pick-up"] },
        // The washers.
        workerWords: { one: "lavador", many: "lavadores" },
        profile: { schemaType: "AutoWash" },
        assetWords: { one: "vehículo", many: "vehículos" },
        categories: {
            IN: ["Lavados", "Ventas", INTEREST_CATEGORY, "Otros"],
            OUT: ["Insumos", SALARY_CATEGORY, "Servicios", "Alquiler", BANK_FEE_CATEGORY, "Otros"],
        },
    },
    "Otro": {
        way: "sells",
        assistantAsks: ["Agrega un producto nuevo", "Juan me quedó debiendo 80", "¿Qué puedes hacer por mí?"],
        listIcon: "grid",
        words: SALE_WORDS,
        profile: { schemaType: "LocalBusiness", tradeName: null },
    },
};
/** A type's way and its data as one profile: the type's words and profile over its way's. */
function joined({ way: id, words, profile, ...data }) {
    const way = WAYS[id];
    return { ...way, ...data, words: { ...way.words, ...words }, profile: { ...way.profile, ...profile } };
}
export const VERTICALS = Object.fromEntries(BUSINESS_TYPES.map((type) => [type, joined(VERTICAL_DATA[type])]));
/** The profile of any string: a type the list does not know reads as `FALLBACK_TYPE`. */
export function verticalOf(type) {
    return VERTICALS[isBusinessType(type) ? type : FALLBACK_TYPE];
}
/** The way a type works: a type the list does not know works as `FALLBACK_TYPE`. */
export function wayOf(type) {
    return VERTICAL_DATA[isBusinessType(type) ? type : FALLBACK_TYPE].way;
}
/** The first type of `BUSINESS_TYPES` on this way: any would do, since every type on a way behaves the same. */
export function representativeOf(way) {
    const first = BUSINESS_TYPES.find((type) => VERTICAL_DATA[type].way === way);
    if (first === undefined)
        throw new Error(`No business type works as «${way}».`);
    return first;
}
/** Whether this vertical's people are the accountants other businesses invite. */
export function isAccountancy(type) {
    return verticalOf(type).accountancy === true;
}
/** The one vertical of accountants: the practice the accountant is offered. */
export function accountancyType() {
    const only = BUSINESS_TYPES.find(isAccountancy);
    if (only === undefined)
        throw new Error("No vertical keeps other businesses' books.");
    return only;
}
/** Which of this trade's documents ask for a review. */
export function reviewsAskedFor(businessType) {
    return verticalOf(businessType).profile.reviews;
}
/** Whether this trade works on its customers' equipment: «Sus equipos» on a ficha, «Equipo» on a document. */
export function servicesAssets(type) {
    return verticalOf(type).servicesAssets;
}
/**
 * Whether this trade keeps a logbook of its customers' objects (spec la
 * bitácora es del taller §3): «Mantenimientos», «Trabajos» on the object's
 * page, its shared history, its photos, a visit's photo on it, and each
 * line's object on a document. A car wash services vehicles only to sell by
 * plate (`servicesAssets`), and keeps none of these.
 */
export function keepsLogbook(type) {
    return verticalOf(type).logbook;
}
/** Whether this trade's closed documents may offer «Agendar otra visita» (`anotherVisit`). */
export function offersAnotherVisit(type) {
    return verticalOf(type).anotherVisit !== false;
}
/** What a trade's asset form offers: its own categories, or every one. */
export function assetCategoriesFor(type) {
    const only = verticalOf(type).assetCategories;
    return only === undefined ? ASSET_CATEGORIES : ASSET_CATEGORIES.filter((c) => only.includes(c.id));
}
/** How this trade sells by plate, or null where «Nueva venta» is the common form. */
export function plateSaleOf(type) {
    return verticalOf(type).plateSale ?? null;
}
function capital(word) {
    return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
}
/** Whoever does this trade's work, in its word; null where it assigns no work. */
export function workerWordsFor(type) {
    const words = verticalOf(type).workerWords;
    if (words === undefined)
        return null;
    return { one: words.one, One: capital(words.one), many: words.many, Many: capital(words.many) };
}
/** The trade's word for the object, capitalised as `DocumentWords` is; null where the trade does not service assets. */
export function assetWordsFor(type) {
    const words = verticalOf(type).assetWords;
    if (words === undefined)
        return null;
    return { one: words.one, One: capital(words.one), many: words.many, Many: capital(words.many) };
}
/**
 * The dictionary's default, as `DEFAULT_WORDS` is for the other words: only
 * a business without the question reads it, and only through the API (`/assets`
 * does not ask `servicesAssets`). No screen draws it.
 */
export const GENERIC_ASSET_WORDS = { one: "equipo", One: "Equipo", many: "equipos", Many: "Equipos" };
/** Every sentence that names the object, in this trade's word: what the server refuses with and what the screens say. */
export function assetSentencesFor(type) {
    return assetSentences(assetWordsFor(type) ?? GENERIC_ASSET_WORDS);
}
